/**
 * Hook for syncing diagrams to cloud storage
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { debounce } from "@excalidraw/common";
import { isInitializedImageElement } from "@excalidraw/element";

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";

import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import {
  createDiagram,
  getDiagram,
  getDiagramForThumbnail,
  getDiagrams,
  updateDiagram,
} from "../data/diagramStorage";
import { LocalData } from "../data/LocalData";
import { isSupabaseConfigured } from "../supabaseClient";
import { generateDiagramThumbnail } from "../utils/generateThumbnail";

import type {
  ChatMessage,
  DiagramCreateResult,
  DiagramListItem,
  DiagramRecord,
} from "../data/diagramStorage";
import type { Session } from "@supabase/supabase-js";

export type SyncStatus = "idle" | "saving" | "saved" | "error";

interface UseDiagramSyncOptions {
  session: Session | null;
  autoSaveDelay?: number;
}

interface UseDiagramSyncReturn {
  // Current diagram
  currentDiagramId: string | null;
  currentDiagramName: string;
  syncStatus: SyncStatus;
  error: string | null;

  // Chat history
  chatHistory: ChatMessage[];

  // Diagram list
  diagrams: DiagramListItem[];
  isLoadingList: boolean;

  // Actions
  loadDiagramList: () => Promise<void>;
  loadDiagram: (id: string) => Promise<DiagramRecord | null>;
  saveToCloud: (
    elements: readonly ExcalidrawElement[],
    appState: Partial<AppState>,
    chatHistory?: ChatMessage[],
    files?: BinaryFiles,
  ) => void;
  saveChatHistory: (chatHistory: ChatMessage[]) => void;
  createNewDiagram: (
    name?: string,
    elements?: readonly ExcalidrawElement[],
  ) => Promise<DiagramCreateResult | null>;
  renameDiagram: (name: string) => Promise<void>;
  renameDiagramById: (id: string, name: string) => Promise<void>;
  setCurrentDiagramId: (id: string | null) => void;
  clearError: () => void;
}

const AUTO_SAVE_DELAY = 3000; // 3 seconds

export const useDiagramSync = ({
  session,
  autoSaveDelay = AUTO_SAVE_DELAY,
}: UseDiagramSyncOptions): UseDiagramSyncReturn => {
  const [currentDiagramId, setCurrentDiagramId] = useState<string | null>(null);
  const [currentDiagramName, setCurrentDiagramName] =
    useState("Untitled Diagram");
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [diagrams, setDiagrams] = useState<DiagramListItem[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const isAuthenticated = Boolean(session?.user);
  // IMPORTANT: Store diagram ID with the pending data to prevent saving to wrong diagram
  // when user switches between projects before debounced save completes
  const pendingSaveRef = useRef<{
    diagramId: string; // Capture the target diagram ID at save request time
    elements: readonly ExcalidrawElement[];
    appState: Partial<AppState>;
    chatHistory?: ChatMessage[];
    files?: BinaryFiles;
  } | null>(null);

  // Use a ref to track currentDiagramId synchronously
  // This prevents race conditions where state updates haven't propagated yet
  const currentDiagramIdRef = useRef<string | null>(null);
  const thumbnailBackfillInFlightRef = useRef<Set<string>>(new Set());
  const generatedThumbnailCacheRef = useRef<Map<string, string>>(new Map());

  // Immediately flush any pending save (used when switching diagrams)
  const flushPendingSave = useCallback(async () => {
    if (!pendingSaveRef.current || !isAuthenticated) {
      return;
    }

    const {
      diagramId,
      elements,
      appState,
      chatHistory: pendingChatHistory,
      files,
    } = pendingSaveRef.current;
    pendingSaveRef.current = null;

    if (!diagramId || elements.length === 0) {
      return;
    }

    if (import.meta.env.DEV) {
      console.log(
        "[useDiagramSync] FLUSHING pending save:",
        elements.length,
        "elements to diagram",
        diagramId,
      );
    }

    try {
      const thumbnail = await generateDiagramThumbnail(
        elements,
        appState,
        files ?? {},
      );
      await updateDiagram(diagramId, {
        elements,
        appState,
        chatHistory: pendingChatHistory,
        thumbnail: thumbnail ?? undefined,
      });
      if (import.meta.env.DEV) {
        console.log("[useDiagramSync] Flush SUCCESS for diagram", diagramId);
      }
    } catch (err) {
      console.error("[useDiagramSync] Flush failed:", err);
    }
  }, [isAuthenticated]);

  // Helper to update both state and ref
  // IMPORTANT: Flushes any pending saves before switching to prevent data loss
  const updateCurrentDiagramId = useCallback(
    (id: string | null) => {
      // If switching to a different diagram, flush pending saves first
      if (
        currentDiagramIdRef.current !== id &&
        currentDiagramIdRef.current !== null
      ) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] Diagram changing from",
            currentDiagramIdRef.current,
            "to",
            id,
          );
        }
        // Fire and forget - we don't want to block the UI
        flushPendingSave();
      }
      currentDiagramIdRef.current = id;
      setCurrentDiagramId(id);
    },
    [flushPendingSave],
  );

  // Best-effort client-side thumbnail fallback for rare missing-preview cases.
  // Keeps current DB flow unchanged and only patches UI experience.
  const backfillMissingThumbnails = useCallback(
    async (list: DiagramListItem[]) => {
      if (!isAuthenticated) {
        return;
      }

      const candidates = list
        .filter(
          (diagram) =>
            !diagram.thumbnail &&
            !generatedThumbnailCacheRef.current.has(diagram.id) &&
            !thumbnailBackfillInFlightRef.current.has(diagram.id),
        )
        .slice(0, 4);

      if (!candidates.length) {
        return;
      }

      await Promise.all(
        candidates.map(async (diagram) => {
          thumbnailBackfillInFlightRef.current.add(diagram.id);
          try {
            const diagramData = await getDiagramForThumbnail(diagram.id);
            if (!diagramData?.elements?.length) {
              return;
            }

            const fileIds =
              diagramData.elements.reduce((acc, element) => {
                if (isInitializedImageElement(element)) {
                  acc.push(element.fileId);
                }
                return acc;
              }, [] as FileId[]) || [];

            let files: BinaryFiles = {};
            if (fileIds.length) {
              const { loadedFiles } = await LocalData.fileStorage.getFiles(
                fileIds,
              );
              if (loadedFiles.length) {
                files = loadedFiles.reduce((acc, file) => {
                  acc[file.id] = file;
                  return acc;
                }, {} as BinaryFiles);
              }
            }

            const thumbnail = await generateDiagramThumbnail(
              diagramData.elements,
              diagramData.app_state || {},
              files,
            );

            if (!thumbnail) {
              return;
            }

            generatedThumbnailCacheRef.current.set(diagram.id, thumbnail);
            setDiagrams((prev) =>
              prev.map((item) =>
                item.id === diagram.id ? { ...item, thumbnail } : item,
              ),
            );
          } catch (err) {
            if (import.meta.env.DEV) {
              console.warn(
                "[useDiagramSync] Thumbnail backfill failed for",
                diagram.id,
                err,
              );
            }
          } finally {
            thumbnailBackfillInFlightRef.current.delete(diagram.id);
          }
        }),
      );
    },
    [isAuthenticated],
  );

  // Load diagram list
  const loadDiagramList = useCallback(async () => {
    if (!isAuthenticated || !isSupabaseConfigured) {
      if (import.meta.env.DEV) {
        console.log(
          "[useDiagramSync] Skipping - not authenticated or Supabase not configured",
        );
      }
      return;
    }

    setIsLoadingList(true);
    try {
      const list = await getDiagrams();
      const mergedList = list.map((diagram) => {
        if (diagram.thumbnail) {
          return diagram;
        }
        const cachedThumbnail = generatedThumbnailCacheRef.current.get(
          diagram.id,
        );
        return cachedThumbnail
          ? { ...diagram, thumbnail: cachedThumbnail }
          : diagram;
      });
      if (import.meta.env.DEV) {
        console.log("[useDiagramSync] Loaded diagrams:", mergedList.length);
      }
      setDiagrams(mergedList);
      void backfillMissingThumbnails(mergedList);
    } catch (err) {
      console.error("[useDiagramSync] Failed to load diagram list:", err);
      setError("Failed to load diagrams");
    } finally {
      setIsLoadingList(false);
    }
  }, [isAuthenticated, session?.user?.id, backfillMissingThumbnails]); // eslint-disable-line react-hooks/exhaustive-deps -- session.user.id is stable for load

  // Load a specific diagram
  const loadDiagram = useCallback(
    async (id: string): Promise<DiagramRecord | null> => {
      if (!isAuthenticated || !isSupabaseConfigured) {
        return null;
      }

      try {
        const diagram = await getDiagram(id);
        if (diagram) {
          updateCurrentDiagramId(diagram.id);
          setCurrentDiagramName(diagram.name);
          setChatHistory(diagram.chat_history || []);
          setSyncStatus("saved");
        }
        return diagram;
      } catch (err) {
        console.error("[useDiagramSync] Failed to load diagram:", err);
        setError("Failed to load diagram");
        return null;
      }
    },
    [isAuthenticated, updateCurrentDiagramId],
  );

  // Debounced save function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce(async () => {
      if (import.meta.env.DEV) {
        console.log("[useDiagramSync] debouncedSave executing...");
        console.log(
          "[useDiagramSync] pendingSaveRef:",
          !!pendingSaveRef.current,
        );
        console.log("[useDiagramSync] isAuthenticated:", isAuthenticated);
      }

      if (!pendingSaveRef.current || !isAuthenticated) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] debouncedSave ABORTED - conditions not met",
          );
        }
        return;
      }

      // CRITICAL: Use the diagram ID that was captured when the save was requested
      // NOT the current diagram ID - this prevents saving to wrong diagram after switching
      const {
        diagramId,
        elements,
        appState,
        chatHistory: pendingChatHistory,
        files,
      } = pendingSaveRef.current;
      pendingSaveRef.current = null;

      if (!diagramId) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] debouncedSave ABORTED - no diagram ID in pending save",
          );
        }
        return;
      }

      if (import.meta.env.DEV) {
        console.log(
          "[useDiagramSync] Saving",
          elements.length,
          "elements to diagram",
          diagramId,
        );
        console.log(
          "[useDiagramSync] Current diagram is:",
          currentDiagramIdRef.current,
        );
        if (diagramId !== currentDiagramIdRef.current) {
          console.log(
            "[useDiagramSync] NOTE: Saving to PREVIOUS diagram (user switched)",
          );
        }
        if (pendingChatHistory) {
          console.log(
            "[useDiagramSync] Saving",
            pendingChatHistory.length,
            "chat messages",
          );
        }
      }

      setSyncStatus("saving");
      try {
        const thumbnail = await generateDiagramThumbnail(
          elements,
          appState,
          files ?? {},
        );
        await updateDiagram(diagramId, {
          elements,
          appState,
          chatHistory: pendingChatHistory,
          thumbnail: thumbnail ?? undefined,
        });
        if (import.meta.env.DEV) {
          console.log("[useDiagramSync] Save SUCCESS to diagram", diagramId);
        }
        setSyncStatus("saved");
      } catch (err) {
        console.error("[useDiagramSync] Auto-save failed:", err);
        setSyncStatus("error");
        setError("Failed to save changes");
      }
    }, autoSaveDelay),
    [isAuthenticated, autoSaveDelay],
  );

  // Save to cloud (debounced)
  const saveToCloud = useCallback(
    (
      elements: readonly ExcalidrawElement[],
      appState: Partial<AppState>,
      chatHistoryToSave?: ChatMessage[],
      files?: BinaryFiles,
    ) => {
      // Use ref for synchronous access to latest diagram ID
      const diagramId = currentDiagramIdRef.current;

      if (!isAuthenticated || !diagramId) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] saveToCloud SKIPPED - no auth or no diagram ID",
          );
        }
        return;
      }

      // Filter out deleted elements - only save visible elements
      const visibleElements = elements.filter((el) => !el.isDeleted);

      // Prevent empty elements from overwriting a pending save with valid elements
      // This can happen when onChange fires during zoom/pan/selection changes
      // IMPORTANT: Only preserve if it's for the SAME diagram - don't mix data between diagrams
      if (
        visibleElements.length === 0 &&
        pendingSaveRef.current &&
        pendingSaveRef.current.elements.length > 0 &&
        pendingSaveRef.current.diagramId === diagramId
      ) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] saveToCloud SKIPPED - empty elements would overwrite",
            pendingSaveRef.current.elements.length,
            "pending elements",
          );
        }
        // Still update appState and chatHistory, but preserve elements (same diagram)
        pendingSaveRef.current = {
          diagramId,
          elements: pendingSaveRef.current.elements,
          appState,
          chatHistory: chatHistoryToSave ?? pendingSaveRef.current.chatHistory,
          files: files ?? pendingSaveRef.current.files,
        };
        return;
      }

      // CRITICAL: Store the diagram ID with the data to ensure it saves to the correct diagram
      // even if the user switches to a different diagram before the debounced save executes
      pendingSaveRef.current = {
        diagramId,
        elements: visibleElements,
        appState,
        chatHistory: chatHistoryToSave,
        files,
      };
      setSyncStatus("saving");
      debouncedSave();
    },
    [isAuthenticated, debouncedSave],
  );

  // Save chat history separately (for when only chat changes)
  const saveChatHistory = useCallback(
    (chatHistoryToSave: ChatMessage[]) => {
      // Use ref for synchronous access to latest diagram ID
      const diagramId = currentDiagramIdRef.current;

      if (import.meta.env.DEV) {
        console.log("[useDiagramSync] saveChatHistory called");
        console.log(
          "[useDiagramSync] chatHistory count:",
          chatHistoryToSave.length,
        );
      }

      if (!isAuthenticated || !diagramId) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] saveChatHistory SKIPPED - no auth or no diagram ID",
          );
        }
        return;
      }

      setChatHistory(chatHistoryToSave);

      // Save immediately without debouncing
      updateDiagram(diagramId, { chatHistory: chatHistoryToSave })
        .then(() => {
          if (import.meta.env.DEV) {
            console.log("[useDiagramSync] Chat history saved successfully");
          }
        })
        .catch((err) => {
          console.error("[useDiagramSync] Failed to save chat history:", err);
        });
    },
    [isAuthenticated],
  );

  // Create new diagram
  const createNewDiagram = useCallback(
    async (
      name?: string,
      elements?: readonly ExcalidrawElement[],
      chatHistory?: ChatMessage[],
    ): Promise<DiagramCreateResult | null> => {
      if (import.meta.env.DEV) {
        console.log("[useDiagramSync] createNewDiagram called");
      }

      if (!isAuthenticated || !isSupabaseConfigured) {
        if (import.meta.env.DEV) {
          console.log(
            "[useDiagramSync] createNewDiagram SKIPPED - not authenticated",
          );
        }
        return null;
      }

      try {
        const diagram = await createDiagram({
          name: name || "Untitled Diagram",
          elements: elements || [],
          chatHistory: chatHistory || [],
        });

        if (import.meta.env.DEV) {
          console.log("[useDiagramSync] Created diagram with ID:", diagram.id);
        }

        updateCurrentDiagramId(diagram.id);
        setCurrentDiagramName(diagram.name);
        setChatHistory(diagram.chat_history || chatHistory || []);
        setSyncStatus("saved");

        // Refresh list
        await loadDiagramList();

        return diagram;
      } catch (err) {
        console.error("[useDiagramSync] Failed to create diagram:", err);
        setError("Failed to create diagram");
        return null;
      }
    },
    [isAuthenticated, loadDiagramList, updateCurrentDiagramId],
  );

  // Rename current diagram
  const renameDiagram = useCallback(
    async (name: string) => {
      // Use ref for synchronous access to latest diagram ID
      const diagramId = currentDiagramIdRef.current;

      if (!isAuthenticated || !diagramId) {
        return;
      }

      try {
        await updateDiagram(diagramId, { name });
        setCurrentDiagramName(name);
        await loadDiagramList();
      } catch (err) {
        console.error("[useDiagramSync] Failed to rename diagram:", err);
        setError("Failed to rename diagram");
      }
    },
    [isAuthenticated, loadDiagramList],
  );

  // Rename a specific diagram by id (more robust than relying on currentDiagramIdRef)
  const renameDiagramById = useCallback(
    async (diagramId: string, name: string) => {
      if (!isAuthenticated || !diagramId) {
        return;
      }

      // Optimistic UI update so Project Shelf reflects immediately
      setDiagrams((prev) =>
        prev.map((d) => (d.id === diagramId ? { ...d, name } : d)),
      );
      if (diagramId === currentDiagramIdRef.current) {
        setCurrentDiagramName(name);
      }

      try {
        await updateDiagram(diagramId, { name });
        await loadDiagramList();
      } catch (err) {
        console.error("[useDiagramSync] Failed to rename diagram by id:", err);
        setError("Failed to rename diagram");
      }
    },
    [isAuthenticated, loadDiagramList],
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load diagram list on authentication
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("[useDiagramSync] Auth effect triggered");
      console.log("[useDiagramSync] isAuthenticated:", isAuthenticated);
    }

    if (isAuthenticated) {
      loadDiagramList();
    } else {
      setDiagrams([]);
      updateCurrentDiagramId(null);
      setCurrentDiagramName("Untitled Diagram");
      setChatHistory([]);
    }
  }, [isAuthenticated, loadDiagramList, updateCurrentDiagramId]);

  return {
    currentDiagramId,
    currentDiagramName,
    chatHistory,
    syncStatus,
    error,
    diagrams,
    isLoadingList,
    loadDiagramList,
    loadDiagram,
    saveToCloud,
    saveChatHistory,
    createNewDiagram,
    renameDiagram,
    renameDiagramById,
    setCurrentDiagramId: updateCurrentDiagramId, // Use the synced version for external use
    clearError,
  };
};
